import { HttpClient, HttpResponse } from '@angular/common/http';
import { addDays, addSeconds, differenceInMilliseconds } from 'date-fns';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PromoCode, SubmitPaymentPlanDetails } from '../model';
import { CacheItem, CacheService } from './cache.service';

export class BaseDataService {

    constructor(protected httpClient: HttpClient, protected cache: CacheService) { }

    private secondsToWaitForPaymentToSubmit = 60;

    setPromoCodeContext(accountId: number | string, promoCode: PromoCode) {
        if (accountId) {
            const codes = this.cache.get(this.cache.items.promoCodes) || {};
            codes[accountId] = promoCode;
            this.cache.set(this.cache.items.promoCodes, codes);
        } else {
            this.cache.set(this.cache.items.promoCodes, null);
        }
    }

    setCustomBingEventsContext(page: string) {
        if (page) {
            const customBingEvents = this.cache.get(this.cache.items.customBingEvents) || {};
            customBingEvents[page] = true;
            this.cache.set(this.cache.items.customBingEvents, customBingEvents);
        } else {
            this.cache.set(this.cache.items.customBingEvents, null);
        }
    }

    public getWaitingForPaymentToSubmitTimeout(payment: string | SubmitPaymentPlanDetails): Promise<boolean> {
        const ms = this.#getWaitingForPaymentToSubmitMilliseconds(payment);
        return new Promise<boolean>(resolve => setTimeout(resolve, ms));
    }

    protected isWaitingForPaymentToSubmit(payment: string | SubmitPaymentPlanDetails): boolean {
        const ms = this.#getWaitingForPaymentToSubmitMilliseconds(payment);
        if (!ms) {
            const acctId = this.#getAcctIdFromPayment(payment);
            this.cache.lastPaymentSubmitTimes[acctId] = new Date();
        }
        return !!ms;
    }

    protected waitingForPaymentToSubmitMessage(): Observable<never> {
        return throwError({ error: 'Waiting for Payment to Process. Please wait a few minutes and try again.'});
    }

    protected getData<T>(cacheKey: string, url: string, options?, refresh: boolean = false, transform?: (value: HttpResponse<any>) => T, cacheScopes?: string[]): Observable<T> {
        return this.cacheData<T>('GET', cacheKey, url, null, options, refresh, transform, cacheScopes);
    }

    protected postData<T>(cacheKey: string, url: string, body: any, options, refresh: boolean = false, transform?: (value: HttpResponse<any>) => T, cacheScopes?: string[]): Observable<T> {
        return this.cacheData<T>('POST', cacheKey, url, body, options, refresh, transform, cacheScopes);
    }

    protected cacheData<T>(verb: string, cacheKey: string, url: string, body: any, options, refresh: boolean = false, transform?: (value: HttpResponse<any>) => T, cacheScopes?: string[]): Observable<T> {
        if (!url.startsWith('https://')) {
            url = `${environment.apiEndpoint}${url}`;
        }

        const opts = options || {};
        opts.observe = 'response';

        if (!transform) {
            transform = (res: HttpResponse<any>) => <T>res.body;
        }
        const trans = (res) => <CacheItem> {
            value: res ? transform(res) : null,
            scopes: cacheScopes
        };

        const action = verb === 'GET' ? () => this.httpClient.get<any>(url, opts) : () => this.httpClient.post<any>(url, body, opts);

        return this.cache.getOrFetch(
            cacheKey,
            action,
            trans,
            refresh);
    }

    #getWaitingForPaymentToSubmitMilliseconds(payment: string | SubmitPaymentPlanDetails): number {
        const acctId = this.#getAcctIdFromPayment(payment);
        const lastTime = this.cache.lastPaymentSubmitTimes[acctId] || addDays(new Date(), -1);
        const nextAvailableTime = addSeconds(lastTime, this.secondsToWaitForPaymentToSubmit);
        const ms = differenceInMilliseconds(nextAvailableTime, new Date());
        return ms > 0 ? ms : 0;
    }

    #getAcctIdFromPayment(payment: string | SubmitPaymentPlanDetails): string {
        if (typeof payment === 'string') {
            return payment;
        }

        const acctId = payment.account?.acctId || payment['acctId'] || 0;
        if (!acctId && payment['payment']) {
            return payment['payment']['acctId'].toString();
        }

        return acctId.toString();
    }

}
